import { z } from "zod";
import { TFunction } from "i18next";
import { EU_EEA_COUNTRIES } from "@/lib/constants/countries";

export const createBaseSchema = (t: TFunction) =>
  z.object({
    salutation: z
      .string()
      .min(1, { message: t("register.validation.salutation.required") }),
    firstName: z
      .string()
      .min(2, { message: t("register.validation.firstName.minLength") }),
    lastName: z
      .string()
      .min(2, { message: t("register.validation.lastName.minLength") }),
    email: z
      .string()
      .email({ message: t("register.validation.email.invalid") }),
    emailConfirm: z
      .string()
      .email({ message: t("register.validation.email.invalid") }),
    telefon: z
      .string()
      .optional(),
    streetAndHouseNumber: z
      .string()
      .min(2, { message: t("register.validation.streetAndHouseNumber.required") }),
    postalCode: z
      .string()
      .min(5, { message: t("register.validation.postalCode.minLength") }),
    city: z
      .string()
      .min(2, { message: t("register.validation.city.required") })
      .regex(/^[a-zA-ZäöüÄÖÜß\s\-'.]+$/, { 
        message: t("register.validation.city.invalid") || "Bitte geben Sie einen gültigen Ortsnamen ein (erlaubt: Buchstaben, Bindestriche, Apostrophe und Leerzeichen)"
      })
      .transform((val) => val.trim()),
    country: z
      .string()
      .min(2, { message: t("register.validation.country.required") })
      .refine((val) => EU_EEA_COUNTRIES.some(country => country.code === val), {
        message: t("register.validation.country.invalid") || "Please select a valid EU/EEA country"
      }),
    dateOfBirth: z.date({
      required_error: t("register.validation.dateOfBirth.required"),
      invalid_type_error: t("register.validation.dateOfBirth.invalid"),
    }),
    password: z
      .string()
      .min(8, { message: t("register.validation.password.minLength") })
      .regex(/[A-Z]/, { message: t("register.validation.password.uppercase") })
      .regex(/[a-z]/, { message: t("register.validation.password.lowercase") })
      .regex(/[0-9]/, { message: t("register.validation.password.number") })
      .regex(/[^A-Za-z0-9]/, {
        message: t("register.validation.password.special"),
      }),
    passwordConfirm: z
      .string()
      .min(8, { message: t("register.validation.password.minLength") }),
    marketingAccepted: z.boolean(),
    privacyAccepted: z.boolean().refine((val) => val === true, {
      message: t("register.validation.privacy.required"),
    }),
  });

export type BaseSchema = ReturnType<typeof createBaseSchema>;
export type RegisterFormData = z.infer<BaseSchema>;

export const createRegisterSchema = (t: TFunction) => {
  return createBaseSchema(t)
    .refine((data) => data.email === data.emailConfirm, {
      message: t("register.validation.email.mismatch"),
      path: ["emailConfirm"],
    })
    .refine((data) => data.password === data.passwordConfirm, {
      message: t("register.validation.password.mismatch"),
      path: ["passwordConfirm"],
    });
};